import { useEffect, useCallback, useRef } from 'react';
import devDebug from '../../Helpers/debug';
import { useComponentsStore } from '../../Stores/componentsStore';
import { useVersionChangeStore, getCalculatedTimeLeft } from '../../Stores/versionChangeStore';
import { useTenderStrategyStore } from '../../Stores/tenderStrategyStore';
import { useAuthStore } from '../../Stores/authStore';
import { removeAllLocalState } from '../../Services/auth';

const VERSION_CHECK_INTERVAL = 1000 * 60 * 5;
const BASE_TIMER_DURATION_MIN = 50;
const BASE_TIMER_DURATION_MAX = 60;

const BUNDLED_LOGIN_DATA_VERSION_FROM_ENV = Number(import.meta.env.VITE_LOGIN_DETAILS_KEY);
const BUNDLED_APP_VERSION_FROM_ENV = import.meta.env.VITE_VERSION;

// Global fetchAppVersion to be used by handleTimerFinished and initial check
const fetchVersionJson = async () => {
  try {
    const response = await fetch(`/version.json?t=${new Date().getTime()}`);
    if (!response.ok) {
      devDebug("Failed to fetch version info:", response.statusText);
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    devDebug("Error fetching version info:", error);
    return null;
  }
};


const useRefreshOnVersionChange = () => {
  const [triggerStrategyReload] = useTenderStrategyStore((state) => [state.triggerStrategyReload]);

  const memoizedFetchAppVersion = useCallback(fetchVersionJson, []);

  const handleTimerFinished = useCallback(async () => {
    devDebug("handleTimerFinished called");
    const storeState = useVersionChangeStore.getState();

    if (!storeState.timerStartTime) {
      devDebug("handleTimerFinished: Timer details already cleared. Aborting.");
      if (storeState.localTimerId) {
        clearInterval(storeState.localTimerId);
        useVersionChangeStore.getState().setLocalTimerId(null);
      }
      return;
    }

    useVersionChangeStore.getState().clearTimerDetails();
    if (storeState.localTimerId) {
      clearInterval(storeState.localTimerId);
      useVersionChangeStore.getState().setLocalTimerId(null);
    }

    const versionData = await fetchVersionJson(); // Re-fetch latest before acting
    devDebug("handleTimerFinished -> Fetched latest versionData: ", versionData);
    if (versionData) {
      // Update store with the absolute latest versions before reload potentially happens
      // This is mostly for other tabs to pick up if they haven't already.
      useVersionChangeStore.getState().setLoginDataVersion(versionData.loginDataVersion);
      useVersionChangeStore.getState().setOtherDataVersion(versionData.otherDataVersion);
    }

    const { user } = useAuthStore.getState();
    const { showRefreshWarningWithLogout } = useComponentsStore.getState();

    if (user?.id) {
      if (showRefreshWarningWithLogout) {
        devDebug("handleTimerFinished -> Logging out user...");
        removeAllLocalState(true); // Logout user
      } else {
        devDebug("handleTimerFinished -> Only removing local state (not auth)...");
        removeAllLocalState(false); // Keep auth, reset other data
        useAuthStore.getState().setStrategiesNeverRequested(true);
        useTenderStrategyStore.getState().setStrategiesStatus("loading");
        useTenderStrategyStore.getState().setTriggerStrategyReload();
      }
    } else {
      devDebug("handleTimerFinished -> no user id found, won't touch local data");
    }

    devDebug("handleTimerFinished -> Reached end of function before reload");
    useComponentsStore.getState().setShowRefreshWarningWithLogout(false);
    useComponentsStore.getState().setShowRefreshWarningWithoutLogout(false);

    window.location.reload();
  }, [triggerStrategyReload]); // Removed triggerStrategyReload as it's accessed via getState


  const startRefreshTimer = useCallback(() => {
    devDebug("startRefreshTimer called");
    const storeState = useVersionChangeStore.getState();

    if (storeState.timerStartTime || storeState.localTimerId) {
      devDebug("startRefreshTimer -> Global timer already active or local interval exists. Not starting new one.");
      if (storeState.timerStartTime && !storeState.localTimerId) {
        devDebug("startRefreshTimer -> Global timer active, but no local interval. Starting local monitoring.");
        const localId = setInterval(() => {
          const timeLeft = getCalculatedTimeLeft(useVersionChangeStore.getState());
          if (timeLeft <= 0) {
            if (useVersionChangeStore.getState().timerStartTime) { // Double check timer still intended
              handleTimerFinished();
            } else { // Timer was cleared by other means
              clearInterval(localId);
              useVersionChangeStore.getState().setLocalTimerId(null);
              devDebug("Local interval: Timer cleared by other means.");
            }
          }
        }, 1000);
        useVersionChangeStore.getState().setLocalTimerId(localId);
      }
      return;
    }

    devDebug("No global timer active, setting new timer interval");
    const newTimerDuration = Math.floor(Math.random() * (BASE_TIMER_DURATION_MAX - BASE_TIMER_DURATION_MIN + 1)) + BASE_TIMER_DURATION_MIN;
    useVersionChangeStore.getState().setTimerDetails(Date.now(), newTimerDuration);

    const localId = setInterval(() => {
      const timeLeft = getCalculatedTimeLeft(useVersionChangeStore.getState());
      if (timeLeft <= 0) {
        if (useVersionChangeStore.getState().timerStartTime) { // Double check timer still intended
          handleTimerFinished();
        } else { // Timer was cleared by other means
          clearInterval(localId);
          useVersionChangeStore.getState().setLocalTimerId(null);
          devDebug("Local interval: Timer cleared by other means before finishing.");
        }
      }
    }, 1000);
    useVersionChangeStore.getState().setLocalTimerId(localId);
  }, [handleTimerFinished]);

  useEffect(() => {
    let pollingIntervalId;

    const checkForUpdates = async () => {
      // Get a snapshot of store state at the beginning of the check
      const storeStateSnapshot = useVersionChangeStore.getState();
      let currentLoginDataVersionInStore = storeStateSnapshot.loginDataVersion;
      let currentOtherDataVersionInStore = storeStateSnapshot.otherDataVersion;
      // Use storeStateSnapshot for timerStartTime and localTimerId for consistency within this check
      const timerStartTimeFromStore = storeStateSnapshot.timerStartTime;
      const localTimerIdFromStore = storeStateSnapshot.localTimerId;


      const serverVersionData = await memoizedFetchAppVersion();
      if (!serverVersionData) {
        devDebug("[Updater] Failed to fetch server version data or server returned null. Aborting check.");
        return;
      }
      devDebug(`[Updater] Fetched server versions: ${JSON.stringify(serverVersionData)}`);
      const serverLoginVersion = serverVersionData.loginDataVersion;
      const serverOtherVersion = serverVersionData.otherDataVersion;

      // Check if the BUNDLED code is already the latest version available on the server.
      const bundledCodeIsCurrentForLogin = (typeof serverLoginVersion === 'undefined' || BUNDLED_LOGIN_DATA_VERSION_FROM_ENV === serverLoginVersion);
      const bundledCodeIsCurrentForOther = (typeof serverOtherVersion === 'undefined' || BUNDLED_APP_VERSION_FROM_ENV === serverOtherVersion);

      if (bundledCodeIsCurrentForLogin && bundledCodeIsCurrentForOther) {
        devDebug(`[Updater] Bundled code version matches or is compatible with server version. App is effectively current.`);
        devDebug(`  Bundled Login: ${BUNDLED_LOGIN_DATA_VERSION_FROM_ENV} (Server: ${serverLoginVersion})`);
        devDebug(`  Bundled Other: ${BUNDLED_APP_VERSION_FROM_ENV} (Server: ${serverOtherVersion})`);

        // Correct the store if it's stale (e.g., after a browser refresh with old localStorage values)
        if (currentLoginDataVersionInStore !== serverLoginVersion) {
          useVersionChangeStore.getState().setLoginDataVersion(serverLoginVersion);
          currentLoginDataVersionInStore = serverLoginVersion; // Update local var for current cycle
          devDebug(`[Updater] Synced store loginDataVersion to ${serverLoginVersion}`);
        }
        if (currentOtherDataVersionInStore !== serverOtherVersion) {
          useVersionChangeStore.getState().setOtherDataVersion(serverOtherVersion);
          currentOtherDataVersionInStore = serverOtherVersion; // Update local var for current cycle
          devDebug(`[Updater] Synced store otherDataVersion to ${serverOtherVersion}`);
        }

        // If the app is current, ensure no refresh warnings are shown and any active timer is cleared.
        if (timerStartTimeFromStore) {
          devDebug("[Updater] App is current, but a timer was active. Clearing timer and warnings.");
          useComponentsStore.getState().setShowRefreshWarningWithLogout(false);
          useComponentsStore.getState().setShowRefreshWarningWithoutLogout(false);
          useVersionChangeStore.getState().clearTimerDetails(); // This will clear timerStartTime
          if (localTimerIdFromStore) {
            clearInterval(localTimerIdFromStore);
            useVersionChangeStore.getState().setLocalTimerId(null);
          }
        }
      }

      const wasLoginVersionInitiallyNull = currentLoginDataVersionInStore === null;
      const wasOtherVersionInitiallyNull = currentOtherDataVersionInStore === null;

      if (wasLoginVersionInitiallyNull || wasOtherVersionInitiallyNull) {
        devDebug("[Updater] Initial population phase. At least one local version is null.");
        let didPopulateLogin = false;
        let didPopulateOther = false;

        if (wasLoginVersionInitiallyNull && typeof serverLoginVersion !== 'undefined') {
          useVersionChangeStore.getState().setLoginDataVersion(serverLoginVersion);
          currentLoginDataVersionInStore = serverLoginVersion;
          didPopulateLogin = true;
          devDebug(`[Updater] Initial: Set loginDataVersion to ${serverLoginVersion}`);
        }
        if (wasOtherVersionInitiallyNull && typeof serverOtherVersion !== 'undefined') {
          useVersionChangeStore.getState().setOtherDataVersion(serverOtherVersion);
          currentOtherDataVersionInStore = serverOtherVersion;
          didPopulateOther = true;
          devDebug(`[Updater] Initial: Set otherDataVersion to ${serverOtherVersion}`);
        }

        if (wasLoginVersionInitiallyNull && wasOtherVersionInitiallyNull && (didPopulateLogin || didPopulateOther)) {
          devDebug("[Updater] Both local versions were initially null and at least one was populated. Skipping further checks for this cycle.");
          return;
        }
        devDebug(`[Updater] Versions after potential initial population for comparison: Login: ${currentLoginDataVersionInStore}, Other: ${currentOtherDataVersionInStore}`);
      }

      // Re-check timer state as it might have been cleared by the "bundled code is current" logic block
      const LATEST_TIMER_START_TIME = useVersionChangeStore.getState().timerStartTime;

      if (!LATEST_TIMER_START_TIME) {
        devDebug(`[Updater] No active global timer. Checking for app/data version updates...`);
        devDebug(`[Updater] Current local versions - Login: ${currentLoginDataVersionInStore}, Other: ${currentOtherDataVersionInStore}`);
        devDebug(`[Updater] Server versions - Login: ${serverLoginVersion}, Other: ${serverOtherVersion}`);


        let newLoginVersionDetected = false;
        if (typeof serverLoginVersion !== 'undefined' && serverLoginVersion !== currentLoginDataVersionInStore) {
          devDebug(`[Updater] New login data version detected. Server: ${serverLoginVersion}, Local: ${currentLoginDataVersionInStore}`);
          useComponentsStore.getState().setShowRefreshWarningWithLogout(true);
          startRefreshTimer();
          newLoginVersionDetected = true;
        }

        if (!newLoginVersionDetected && typeof serverOtherVersion !== 'undefined' && serverOtherVersion !== currentOtherDataVersionInStore) {
          devDebug(`[Updater] New other data version detected. Server: ${serverOtherVersion}, Local: ${currentOtherDataVersionInStore}`);
          useComponentsStore.getState().setShowRefreshWarningWithoutLogout(true);
          startRefreshTimer();
        }

      } else { // Global timer is active
        devDebug(`[Updater] Global refresh timer is active (Started at: ${new Date(LATEST_TIMER_START_TIME).toLocaleTimeString()}).`);
        devDebug(`[Updater] Current local versions (while timer active) - Login: ${currentLoginDataVersionInStore}, Other: ${currentOtherDataVersionInStore}`);

        // Ensure this tab has a local interval to act when timer finishes
        const currentLocalTimerId = useVersionChangeStore.getState().localTimerId; // Re-fetch
        if (!currentLocalTimerId) {
          devDebug("[Updater] Global timer active, but this tab has no local monitoring interval. Starting one.");
          startRefreshTimer();
        }

        const loginMatches = (typeof serverLoginVersion === 'undefined') || (serverLoginVersion === currentLoginDataVersionInStore);
        const otherMatches = (typeof serverOtherVersion === 'undefined') || (serverOtherVersion === currentOtherDataVersionInStore);

        if (loginMatches && otherMatches) {
          devDebug("[Updater] Local versions now match server versions (or server provides no conflicting version). Clearing warnings and timer.");
          useComponentsStore.getState().setShowRefreshWarningWithLogout(false);
          useComponentsStore.getState().setShowRefreshWarningWithoutLogout(false);

          useVersionChangeStore.getState().clearTimerDetails();
          const finalLocalTimerId = useVersionChangeStore.getState().localTimerId;
          if (finalLocalTimerId) {
            clearInterval(finalLocalTimerId);
            useVersionChangeStore.getState().setLocalTimerId(null);
          }
        } else {
          devDebug(`[Updater] Versions still mismatch or server has newer. Timer remains active. Login Match: ${loginMatches}, Other Match: ${otherMatches}`);
        }
      }
    };

    if (typeof window !== 'undefined') {
      checkForUpdates(); // Initial check
      pollingIntervalId = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);
    }

    return () => {
      clearInterval(pollingIntervalId);
      // Clearing localTimerId is generally handled by handleTimerFinished or when versions match.
      // Avoid clearing it here directly as it might be managed by other logic or other tabs.
      // The logic within handleTimerFinished, startRefreshTimer, and checkForUpdates should manage clearing.
      devDebug("[Effect Cleanup] Main polling interval cleared for useRefreshOnVersionChange instance.");
    };
  }, [memoizedFetchAppVersion, startRefreshTimer, handleTimerFinished]); // Removed triggerStrategyReload, accessed via getState

  // This secondary effect is to ensure that if a global timer is set (e.g., by another tab),
  // this tab starts its local monitoring interval.
  useEffect(() => {
    const { timerStartTime, localTimerId } = useVersionChangeStore.getState();

    if (timerStartTime && !localTimerId) {
      devDebug("[Secondary Effect] Global timer active, but no local monitoring for this tab. Starting local monitoring.");
      startRefreshTimer(); // This will correctly start local monitoring if global timer is set by another tab.
    }
    // No specific cleanup needed for this effect beyond what startRefreshTimer itself manages.
  }, [startRefreshTimer]); // Depends on startRefreshTimer to correctly handle its logic

  // No return value from the hook, it operates via side effects and store changes.
};

export default useRefreshOnVersionChange;